import PropTypes from "prop-types";
import React, { useState } from "react";
import styled from "styled-components";
import HeadingTwo from "../../components/HeadingTwo";
import StyledButton from "../../components/StyledButton";
import colors from "../../constants/colors";
import constants from "../../constants/constants";
import styl from "../../styl";
import AboAccordion from "./AboAccordion";

// "1.234,50" -> 1234.5
const parsePrice = (price) =>
  parseFloat((price || "0").replace(/\./g, "").replace(",", "."));

// Anzeigename der Variante, z.B. "3 Monate"
export const getVariationLabel = (variation) =>
  variation.attributes?.nodes?.map((attribute) => attribute.value).join(", ") ||
  variation.name;

// Feste Infos zum Abo – die Beschreibung kommt aus dem Backend
const aboInfos = [
  {
    title: "Versandinformationen",
    content: (
      <>
        <p>
          Dein Abo wird während der gewählten Laufzeit einmal im Monat als
          Paket an deine Lieferadresse verschickt. Die erste Lieferung geht
          [im Folgemonat nach Zahlungseingang] auf die Reise.
        </p>
        <p>
          Abos werden ohne Kühlbox versendet. [Versandkosten sind im Preis
          enthalten.]
        </p>
      </>
    ),
  },
  {
    title: "Abopause und Urlaub",
    content: (
      <p>
        Du bist einmal nicht zu Hause? Schreib uns bis spätestens [X Tage] vor
        dem nächsten Versand an{" "}
        <a href="mailto:info@gutgereift.at">info@gutgereift.at</a>, dann
        verschieben wir deine Lieferung.
      </p>
    ),
  },
  {
    title: "Stell uns eine Frage",
    content: (
      <p>
        Du hast Fragen zum Abo? Wir sind persönlich für dich da:
        <br />
        E-Mail: <a href="mailto:info@gutgereift.at">info@gutgereift.at</a>
        <br />
        Tel.: <a href="tel:+436644142827">+43 664 414 28 27</a>
      </p>
    ),
  },
];

/**
 * @author Sandro Mennel
 * @param {object} product the abo product from the backend (required)
 * @param {number} initialVariationId variation to preselect, e.g. after login
 * @param {function} onOrder called with (product, variation) when ordering (required)
 * @param {boolean} loading shows the loading state on the order button
 * @example <AboProduct product={product} onOrder={(product, variation) => {}} />
 */
export default function AboProduct({
  product,
  initialVariationId,
  onOrder,
  loading,
}) {
  const variations = [...(product.variations?.nodes || [])].sort(
    (a, b) => parsePrice(a.price) - parsePrice(b.price)
  );
  // Bilder, die nicht geladen werden können (z.B. 404), werden ausgeblendet
  const [brokenImages, setBrokenImages] = useState([]);
  const images = [
    product.image,
    ...(product.galleryImages?.nodes || []),
  ].filter(
    (image) => image?.sourceUrl && !brokenImages.includes(image.sourceUrl)
  );
  const handleImageError = (sourceUrl) =>
    setBrokenImages((prev) => [...prev, sourceUrl]);

  const [selectedVariationId, setSelectedVariationId] = useState(
    variations.some((v) => v.variationId === initialVariationId)
      ? initialVariationId
      : variations[0]?.variationId
  );
  const [selectedImage, setSelectedImage] = useState(0);

  const currentImage = images[Math.min(selectedImage, images.length - 1)];

  const selectedVariation = variations.find(
    (v) => v.variationId === selectedVariationId
  );

  return (
    <Main>
      <Gallery>
        {currentImage && (
          <MainImage
            src={currentImage.sourceUrl}
            alt={currentImage.altText || product.name}
            onError={() => handleImageError(currentImage.sourceUrl)}
          />
        )}
        {images.length > 1 && (
          <Thumbnails>
            {images.map((image, i) => (
              <Thumbnail
                key={image.sourceUrl + i}
                src={image.sourceUrl}
                alt={image.altText || product.name}
                $selected={image === currentImage}
                onClick={() => setSelectedImage(i)}
                onError={() => handleImageError(image.sourceUrl)}
              />
            ))}
          </Thumbnails>
        )}
      </Gallery>

      <Info>
        <HeadingTwo title={product.name} />
        {selectedVariation && <Price>€ {selectedVariation.price}</Price>}

        {variations.length > 0 && (
          <>
            <Label>Laufzeit</Label>
            <Options>
              {variations.map((variation) => (
                <Option
                  key={variation.variationId}
                  $selected={variation.variationId === selectedVariationId}
                  onClick={() => setSelectedVariationId(variation.variationId)}
                >
                  {getVariationLabel(variation)}
                </Option>
              ))}
            </Options>
          </>
        )}

        <ButtonBox>
          <StyledButton
            title={"Zahlungspflichtig bestellen"}
            color={colors.YellowBrigthButton}
            scale={"BIG"}
            fontSize={"21px"}
            loading={loading}
            onClick={() =>
              selectedVariation && onOrder(product, selectedVariation)
            }
          />
        </ButtonBox>

        <AboAccordion
          items={[
            ...(product.description
              ? [{ title: "Beschreibung", html: product.description }]
              : []),
            ...aboInfos,
          ]}
        />
      </Info>
    </Main>
  );
}

AboProduct.propTypes = {
  product: PropTypes.object.isRequired,
  initialVariationId: PropTypes.number,
  onOrder: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const Main = styled.div`
  ${styl.flexRow};
  align-items: flex-start;
  gap: 80px;
  margin-top: 40px;
  margin-bottom: 60px;
  max-width: 1600px;

  @media (max-width: 1100px) {
    flex-direction: column;
    gap: 30px;
  }
`;

// Galerie bleibt beim Scrollen durch die Beschreibung sichtbar (unter dem Header)
const Gallery = styled.div`
  ${styl.flexColumn};
  flex: 1 1 55%;
  max-width: 850px;
  position: sticky;
  top: 140px;

  @media (max-width: 1100px) {
    position: static;
    width: 100%;
    max-width: 600px;
  }
`;

const MainImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
`;

const Thumbnails = styled.div`
  ${styl.flexRow};
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const Thumbnail = styled.img`
  width: 90px;
  height: 90px;
  object-fit: cover;
  cursor: pointer;
  box-sizing: border-box;
  border: 2px solid
    ${(props) => (props.$selected ? colors.OldgoldYellowLogo : "transparent")};

  @media (max-width: ${constants.iPhonePortraitWidth}px) {
    width: 60px;
    height: 60px;
  }
`;

const Info = styled.div`
  ${styl.flexColumn};
  flex: 1 1 45%;
  max-width: 680px;
`;

const Price = styled.p`
  ${styl.boldDefaultFont};
  font-size: 24px;
  color: ${colors.SolidBlackText};
  margin: 20px 0px 0px 0px;
`;

const Label = styled.p`
  ${styl.lightDefaultFont};
  font-size: 14px;
  color: ${colors.MineshaftGreyText};
  margin: 25px 0px 8px 0px;
`;

const Options = styled.div`
  ${styl.flexRow};
  flex-wrap: wrap;
  gap: 10px;
`;

const Option = styled.button`
  ${styl.clear};
  ${styl.lightDefaultFont};
  font-size: 14px;
  padding: 10px 18px;
  cursor: pointer;
  color: ${(props) =>
    props.$selected ? colors.SolidWhiteBackground : colors.SolidBlackText};
  background: ${(props) =>
    props.$selected ? colors.YellowBrigthButton : colors.SolidWhiteBackground};
  ${styl.transition};

  &:hover {
    background: ${(props) =>
      props.$selected
        ? colors.YellowBrigthButton
        : colors.HamptonYellowHomepageSquare};
  }
`;

const ButtonBox = styled.div`
  margin-top: 30px;
`;
